import React, { useState, useEffect } from 'react';
import {
  Button,
  Container,
  TextField,
  Table,
  Modal,
  TableBody,
  TableCell,
  Box,
  Typography,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  InputAdornment,
  Stack,
  FormControl,
  styled,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  IconButton,
  useTheme,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Print as PrintIcon,
  KeyboardDoubleArrowLeft as ChevronLeft,
  KeyboardDoubleArrowRight as ChevronRight,
  Close as CloseIcon,
} from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import { useNavigate } from 'react-router-dom';
import { decimalOnly, digitsOnly } from '../../../utils/numericInput';

const CustomTableScrollbar = styled('div')(({ theme }) => ({
  '&::-webkit-scrollbar': {
    height: '6px',
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
  },
  scrollbarWidth: 'thin',
  scrollbarColor: `${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'} transparent`,
}));

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const EnquiryQuotation = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddQuotationModal, setShowAddQuotationModal] = useState(false);
  const [showEditQuotationModal, setShowEditQuotationModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successModalType, setSuccessModalType] = useState('');
  const [selectedQuotation, setSelectedQuotation] = useState(null);

  const staticQuotationData = [
    { quotationId: 1, quotationNo: 'Q001', candidateName: 'John Doe', quotationDate: '2023-10-01', emailId: 'john.doe@example.com', mobileNo: '1234567890', noOfPersons: 1, noOfSessions: 5, totalFee: 500 },
    { quotationId: 2, quotationNo: 'Q002', candidateName: 'Jane Smith', quotationDate: '2023-10-02', emailId: 'jane.smith@example.com', mobileNo: '9876543210', noOfPersons: 2, noOfSessions: 10, totalFee: 1000 },
    { quotationId: 3, quotationNo: 'Q003', candidateName: 'Alice Johnson', quotationDate: '2023-10-03', emailId: 'alice.j@example.com', mobileNo: '5551234567', noOfPersons: 1, noOfSessions: 3, totalFee: 300 },
    { quotationId: 4, quotationNo: 'Q004', candidateName: 'Bob Brown', quotationDate: '2023-10-04', emailId: 'bob.brown@example.com', mobileNo: '4449876543', noOfPersons: 3, noOfSessions: 8, totalFee: 800 },
    { quotationId: 5, quotationNo: 'Q005', candidateName: 'Charlie Davis', quotationDate: '2023-10-05', emailId: 'charlie.d@example.com', mobileNo: '7776665555', noOfPersons: 1, noOfSessions: 4, totalFee: 400 },
    { quotationId: 6, quotationNo: 'Q006', candidateName: 'Diana Evans', quotationDate: '2023-10-06', emailId: 'diana.e@example.com', mobileNo: '3332221111', noOfPersons: 2, noOfSessions: 6, totalFee: 600 },
  ];

  const [quotations, setQuotations] = useState(staticQuotationData);
  const [filteredData, setFilteredData] = useState(staticQuotationData);

  const handleAddQuotation = () => {
    setShowAddQuotationModal(true);
  };

  const handleEdit = (quotation) => {
    setSelectedQuotation(quotation);
    setShowEditQuotationModal(true);
  };

  const handleDelete = (quotation) => {
    setSelectedQuotation(quotation);
    setShowDeleteModal(true);
  };

  const handlePrint = (quotationId) => {
    setSnackbarMessage(`Printing quotation with ID: ${quotationId}`);
    setOpenSnackbar(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedQuotation(null);
  };

  const handleConfirmDelete = () => {
    setQuotations((prevQuotations) =>
      prevQuotations.filter((q) => q.quotationId !== selectedQuotation.quotationId)
    );
    setSnackbarMessage(`Quotation ${selectedQuotation.quotationNo} deleted successfully!`);
    setOpenSnackbar(true);
    setShowDeleteModal(false);
    setSelectedQuotation(null);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSaveQuotation = () => {
    setShowAddQuotationModal(false);
    setSuccessModalType('add');
    setShowSuccessModal(true);
  };

  const handleUpdateQuotation = () => {
    setShowEditQuotationModal(false);
    setSuccessModalType('edit');
    setShowSuccessModal(true);
  };

  useEffect(() => {
    setFilteredData(
      quotations.filter(
        (quotation) =>
          quotation.quotationNo.toLowerCase().includes(searchTerm.toLowerCase()) ||
          quotation.candidateName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          quotation.emailId.toLowerCase().includes(searchTerm.toLowerCase()) ||
          quotation.mobileNo.toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [searchTerm, quotations]);

  const paginatedData = filteredData.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  return (
    <Container maxWidth={false} sx={{ width: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mb: 1,
          p: 2,
          boxShadow: 'none',
          maxWidth: '100%',
          boxSizing: 'border-box',
        }}
      >
        <TextField
          variant="outlined"
          placeholder="Search..."
          value={searchTerm}
          onChange={handleSearch}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <SearchIcon />
              </InputAdornment>
            ),
            sx: {
              borderRadius: '10px',
            }
          }}
          sx={{ width: 300, maxWidth: '100%' }}
        />
        <Button
          variant="contained"
          onClick={handleAddQuotation}
          sx={{
            ml: 2,
            width: '200px',
            height: '50px',
            borderRadius: '10px',
            padding: '10px 0',
            fontWeight: 'bold',
            backgroundColor: theme.palette.primary.main,
            '&:hover': { backgroundColor: theme.palette.primary.dark },
          }}
          startIcon={<AddIcon />}
        >
          ADD QUOTATION
        </Button>
      </Box>

      <Paper
        elevation={3}
        sx={{
          paddingBottom: 0,
          maxWidth: '100%',
          boxSizing: 'border-box',
          mx: 'auto',
          backgroundColor: theme.palette.background.paper,
          borderRadius: '16px',
        }}
      >
        <CustomTableScrollbar sx={{ width: '100%', overflowX: 'auto' }}>
          <TableContainer sx={{ width: '100%', borderRadius: '16px' }}>
            <Table
              sx={{
                minWidth: 1200,
                '& .MuiTableHead-root .MuiTableCell-root': {
                  fontWeight: 'bold',
                  backgroundColor: theme.palette.primary.main,
                  color: 'white',
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem', minWidth: 70, whiteSpace: 'nowrap' }}>S.No</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem', minWidth: 120, whiteSpace: 'nowrap' }}>Quotation No.</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem', minWidth: 180, whiteSpace: 'nowrap' }}>Candidate Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem', minWidth: 150, whiteSpace: 'nowrap' }}>Quotation Date</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem', minWidth: 200, whiteSpace: 'nowrap' }}>Email ID</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem', minWidth: 150, whiteSpace: 'nowrap' }}>Mobile No.</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem', minWidth: 100, whiteSpace: 'nowrap' }}>No. of Persons</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem', minWidth: 100, whiteSpace: 'nowrap' }}>No. of Sessions</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem', minWidth: 100, whiteSpace: 'nowrap' }}>Total Fee</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: theme.palette.primary.dark, fontSize: '1.1rem', minWidth: 120, whiteSpace: 'nowrap' }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.map((quotation, index) => (
                  <TableRow
                    key={quotation.quotationId}
                    sx={{
                      backgroundColor: index % 2 === 0 ? theme.palette.background.paper : theme.palette.background.paper,
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      },
                    }}
                  >
                    <TableCell sx={{ fontSize: '1rem', whiteSpace: 'nowrap' }}>{(page - 1) * rowsPerPage + index + 1}</TableCell>
                    <TableCell sx={{ fontSize: '1rem', whiteSpace: 'nowrap' }}>{quotation.quotationNo}</TableCell>
                    <TableCell sx={{ fontSize: '1rem', whiteSpace: 'nowrap' }}>{quotation.candidateName}</TableCell>
                    <TableCell sx={{ fontSize: '1rem', whiteSpace: 'nowrap' }}>{quotation.quotationDate}</TableCell>
                    <TableCell sx={{ fontSize: '1rem', whiteSpace: 'nowrap' }}>{quotation.emailId}</TableCell>
                    <TableCell sx={{ fontSize: '1rem', whiteSpace: 'nowrap' }}>{quotation.mobileNo}</TableCell>
                    <TableCell sx={{ fontSize: '1rem', whiteSpace: 'nowrap' }}>{quotation.noOfPersons}</TableCell>
                    <TableCell sx={{ fontSize: '1rem', whiteSpace: 'nowrap' }}>{quotation.noOfSessions}</TableCell>
                    <TableCell sx={{ fontSize: '1rem', whiteSpace: 'nowrap' }}>{quotation.totalFee}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        onClick={() => handlePrint(quotation.quotationId)}
                        sx={{
                          color: theme.palette.primary.light,
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                          },
                        }}
                      >
                        <PrintIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => handleEdit(quotation)}
                        sx={{
                          color: theme.palette.primary.light,
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                          },
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => handleDelete(quotation)}
                        sx={{
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                          },
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CustomTableScrollbar>
      </Paper>

      {filteredData.length > 0 && (
        <Box
          sx={{
            maxWidth: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 1,
            p: 2,
            mx: 'auto',
            pt: 2,
          }}
        >
          <Typography variant="body2" color={theme.palette.primary.main}>
            Page <strong>{page}</strong> of <strong>{Math.ceil(filteredData.length / rowsPerPage)}</strong>
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton
              onClick={() => handleChangePage(null, page - 1)}
              disabled={page === 1}
              sx={{
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
                '&.Mui-disabled': {
                  opacity: 0.3,
                },
                borderRadius: '50%',
                width: '36px',
                height: '36px',
              }}
            >
              <ChevronLeft />
            </IconButton>
            {[...Array(Math.ceil(filteredData.length / rowsPerPage))].map((_, index) => (
              <IconButton
                key={index + 1}
                onClick={() => handleChangePage(null, index + 1)}
                sx={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  backgroundColor: page === index + 1 ? theme.palette.primary.main : 'transparent',
                  color: page === index + 1 ? theme.palette.primary.contrastText : theme.palette.text.primary,
                  '&:hover': {
                    backgroundColor: page === index + 1 ? theme.palette.primary.dark : theme.palette.action.hover,
                  },
                }}
              >
                {index + 1}
              </IconButton>
            ))}
            <IconButton
              onClick={() => handleChangePage(null, page + 1)}
              disabled={page >= Math.ceil(filteredData.length / rowsPerPage)}
              sx={{
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
                '&.Mui-disabled': {
                  opacity: 0.3,
                },
                borderRadius: '50%',
                width: '36px',
                height: '36px',
              }}
            >
              <ChevronRight />
            </IconButton>
            <FormControl size="small" sx={{ minWidth: 100, ml: 1 }}>
              <InputLabel id="rows-per-page-label" sx={{ color: theme.palette.text.primary }}>Rows</InputLabel>
              <Select
                labelId="rows-per-page-label"
                value={rowsPerPage}
                onChange={handleChangeRowsPerPage}
                label="Rows"
                sx={{
                  height: '36px',
                  color: theme.palette.text.primary,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.text.primary,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                  },
                }}
              >
                <MenuItem value={5}>5</MenuItem>
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={30}>30</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Box>
      )}

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity="success" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Add Quotation Modal */}
      <AddQuotationModal
        open={showAddQuotationModal}
        onClose={() => setShowAddQuotationModal(false)}
        onSuccess={handleSaveQuotation}
      />

      {/* Edit Quotation Modal */}
      <EditQuotationModal
        open={showEditQuotationModal}
        onClose={() => setShowEditQuotationModal(false)}
        onSuccess={handleUpdateQuotation}
        quotation={selectedQuotation}
      />

      {/* Delete Modal */}
      <Modal
        open={showDeleteModal}
        onClose={handleCloseDeleteModal}
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ backgroundColor: theme.palette.primary.main, padding: '12px 16px' }}>
            <Typography id="delete-modal-title" variant="h6" sx={{ color: '#fff' }}>
              Delete Confirmation ⚠️
            </Typography>
          </Box>
          <Box sx={{ padding: '20px' }}>
            <Typography id="delete-modal-description" sx={{ mb: 2 }}>
              Are you sure you want to delete quotation <strong>{selectedQuotation?.quotationNo}</strong>?
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button onClick={handleCloseDeleteModal} variant="outlined" sx={{ mr: 1 }} data-app-cancel="true">
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleConfirmDelete}
              >
                Yes, Delete
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* Success Modal */}
      <Modal
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ backgroundColor: theme.palette.primary.main, padding: '12px 16px' }}>
            <Typography id="success-modal-title" variant="h6" sx={{ color: '#fff' }}>
              {successModalType === 'add' ? 'Quotation Added ✅' : 'Quotation Updated ✅'}
            </Typography>
          </Box>
          <Box sx={{ padding: '20px' }}>
            <Typography id="success-modal-description" sx={{ mb: 2 }}>
              {successModalType === 'add' ? 'Quotation Added Successfully' : 'Quotation Updated Successfully'}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button
                onClick={() => setShowSuccessModal(false)}
                variant="contained"
                color="primary"
              >
                OK
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
    </Container>
  );
};

// AddQuotationModal Component
const AddQuotationModal = ({ open, onClose, onSuccess }) => {
  const theme = useTheme();
  const [quotationDate, setQuotationDate] = useState('');
  const [candidateName, setCandidateName] = useState('');
  const [mobileNo, setMobileNo] = useState('');
  const [email, setEmail] = useState('');
  const [countryId, setCountryId] = useState('');
  const [stateId, setStateId] = useState('');
  const [cityId, setCityId] = useState('');
  const [noOfPerson, setNoOfPerson] = useState('');
  const [noOfSession, setNoOfSession] = useState('');
  const [totalFee, setTotalFee] = useState('');
  const [course, setCourse] = useState('');

  const staticCountryData = [
    { countryId: 1, countryName: 'India' },
  ];
  const staticStateData = [
    { stateId: 1, stateName: 'Bihar' },
    { stateId: 2, stateName: 'Jharkhand' },
  ];
  const staticCityData = [
    { cityId: 1, cityName: 'Patna' },
    { cityId: 2, cityName: 'Ranchi' },
  ];

  useEffect(() => {
    const today = new Date();
    const formattedDate = today.toISOString().split('T')[0];
    setQuotationDate(formattedDate);
  }, []);

  const handleSave = (e) => {
    e.preventDefault();
    if (candidateName && mobileNo && email && countryId && stateId && cityId && noOfPerson && noOfSession && totalFee && course) {
      onSuccess();
      handleCancel();
    } else {
      alert('Please fill all fields');
    }
  };

  const handleCancel = () => {
    setCandidateName('');
    setMobileNo('');
    setEmail('');
    setCountryId('');
    setStateId('');
    setCityId('');
    setNoOfPerson('');
    setNoOfSession('');
    setTotalFee('');
    setCourse('');
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
    >
      <CustomTableScrollbar
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 700,
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          overflow: 'hidden',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            backgroundColor: theme.palette.primary.main,
            padding: '12px 16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            position: 'sticky',
            top: 0,
            zIndex: 1,
          }}
        >
          <Typography variant="h6" sx={{ color: '#fff', fontWeight: 'bold' }}>
            Add Enquiry Quotation
          </Typography>
          <IconButton onClick={onClose} sx={{ color: '#fff' }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ padding: '20px', overflowY: 'auto', flexGrow: 1 }}>
          <form onSubmit={handleSave}>
            <TextField
              label="Quotation Date"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={quotationDate}
              onChange={(e) => setQuotationDate(e.target.value)}
              sx={{
                mb: 3,
                '& .MuiInputLabel-root': { color: theme.palette.text.primary },
                '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.primary.main },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
              }}
            />
            <TextField
              label="Candidate Name"
              variant="outlined"
              fullWidth
              value={candidateName}
              onChange={(e) => setCandidateName(e.target.value)}
              sx={{
                mb: 3,
                '& .MuiInputLabel-root': { color: theme.palette.text.primary },
                '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.primary.main },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
              }}
            />
            <TextField
              label="Mobile No."
              variant="outlined"
              fullWidth
              value={mobileNo}
              onChange={(e) => setMobileNo(e.target.value)}
              sx={{
                mb: 3,
                '& .MuiInputLabel-root': { color: theme.palette.text.primary },
                '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.primary.main },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
              }}
            />
            <TextField
              label="Email"
              variant="outlined"
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={{
                mb: 3,
                '& .MuiInputLabel-root': { color: theme.palette.text.primary },
                '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.primary.main },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
              }}
            />
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id="country-label" sx={{ color: theme.palette.text.primary }}>Country</InputLabel>
              <Select
                labelId="country-label"
                id="country"
                value={countryId}
                label="Country"
                onChange={(e) => setCountryId(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
                }}
              >
                {staticCountryData.map((countryItem) => (
                  <MenuItem key={countryItem.countryId} value={countryItem.countryId}>
                    {countryItem.countryName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id="state-label" sx={{ color: theme.palette.text.primary }}>State</InputLabel>
              <Select
                labelId="state-label"
                id="state"
                value={stateId}
                label="State"
                onChange={(e) => setStateId(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
                }}
              >
                {staticStateData.map((stateItem) => (
                  <MenuItem key={stateItem.stateId} value={stateItem.stateId}>
                    {stateItem.stateName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id="city-label" sx={{ color: theme.palette.text.primary }}>City</InputLabel>
              <Select
                labelId="city-label"
                id="city"
                value={cityId}
                label="City"
                onChange={(e) => setCityId(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
                }}
              >
                {staticCityData.map((cityItem) => (
                  <MenuItem key={cityItem.cityId} value={cityItem.cityId}>
                    {cityItem.cityName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="No. of Person"
              variant="outlined"
              fullWidth
              value={noOfPerson}
              onChange={(e) => setNoOfPerson(digitsOnly(e.target.value))}
              inputProps={{ inputMode: 'numeric' }}
              sx={{
                mb: 3,
                '& .MuiInputLabel-root': { color: theme.palette.text.primary },
                '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.primary.main },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
              }}
            />
            <TextField
              label="No. of Session"
              variant="outlined"
              fullWidth
              value={noOfSession}
              onChange={(e) => setNoOfSession(digitsOnly(e.target.value))}
              inputProps={{ inputMode: 'numeric' }}
              sx={{
                mb: 3,
                '& .MuiInputLabel-root': { color: theme.palette.text.primary },
                '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.primary.main },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
              }}
            />
            <TextField
              label="Total Fee"
              variant="outlined"
              fullWidth
              value={totalFee}
              onChange={(e) => setTotalFee(decimalOnly(e.target.value))}
              inputProps={{ inputMode: 'decimal' }}
              sx={{
                mb: 3,
                '& .MuiInputLabel-root': { color: theme.palette.text.primary },
                '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.primary.main },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
              }}
            />
            <TextField
              label="Course"
              variant="outlined"
              fullWidth
              value={course}
              onChange={(e) => setCourse(e.target.value)}
              sx={{
                mb: 3,
                '& .MuiInputLabel-root': { color: theme.palette.text.primary },
                '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.primary.main },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Tooltip
                title={
                  !candidateName ? "Enter Candidate Name" :
                  !mobileNo ? "Enter Mobile No." :
                  !email ? "Enter Email" :
                  !countryId ? "Select Country" :
                  !stateId ? "Select State" :
                  !cityId ? "Select City" :
                  !noOfPerson ? "Enter No. of Person" :
                  !noOfSession ? "Enter No. of Session" :
                  !totalFee ? "Enter Total Fee" : "Enter Course"
                }
                arrow
              >
                <span>
                  <Button
                    variant="contained"
                    onClick={handleSave}
                    sx={{
                      mr: 2,
                      backgroundColor: theme.palette.primary.main,
                      '&:hover': { backgroundColor: theme.palette.primary.dark },
                    }}
                    disabled={!candidateName || !mobileNo || !email || !countryId || !stateId || !cityId || !noOfPerson || !noOfSession || !totalFee || !course}
                  >
                    SAVE
                  </Button>
                </span>
              </Tooltip>
              <Button
                variant="contained"
                color="error"
                onClick={handleCancel}
              >
                RESET
              </Button>
            </Box>
          </form>
        </Box>
      </CustomTableScrollbar>
    </Modal>
  );
};

// EditQuotationModal Component
const EditQuotationModal = ({ open, onClose, onSuccess, quotation }) => {
  const theme = useTheme();
  const [quotationDate, setQuotationDate] = useState('');
  const [candidateName, setCandidateName] = useState('');
  const [mobileNo, setMobileNo] = useState('');
  const [email, setEmail] = useState('');
  const [countryId, setCountryId] = useState('');
  const [stateId, setStateId] = useState('');
  const [cityId, setCityId] = useState('');
  const [noOfPerson, setNoOfPerson] = useState('');
  const [noOfSession, setNoOfSession] = useState('');
  const [totalFee, setTotalFee] = useState('');
  const [course, setCourse] = useState('');

  const staticCountryData = [
    { countryId: 1, countryName: 'India' },
  ];
  const staticStateData = [
    { stateId: 1, stateName: 'Bihar' },
    { stateId: 2, stateName: 'Jharkhand' },
  ];
  const staticCityData = [
    { cityId: 1, cityName: 'Patna' },
    { cityId: 2, cityName: 'Ranchi' },
  ];

  useEffect(() => {
    if (quotation) {
      setQuotationDate(quotation.quotationDate);
      setCandidateName(quotation.candidateName);
      setMobileNo(quotation.mobileNo);
      setEmail(quotation.emailId);
      setNoOfPerson(quotation.noOfPersons);
      setNoOfSession(quotation.noOfSessions);
      setTotalFee(quotation.totalFee);
    }
  }, [quotation]);

  const handleSave = (e) => {
    e.preventDefault();
    if (candidateName && mobileNo && email && countryId && stateId && cityId && noOfPerson && noOfSession && totalFee && course) {
      onSuccess();
      onClose();
    } else {
      alert('Please fill all fields');
    }
  };

  const handleCancel = () => {
    if (quotation) {
      setQuotationDate(quotation.quotationDate);
      setCandidateName(quotation.candidateName);
      setMobileNo(quotation.mobileNo);
      setEmail(quotation.emailId);
      setNoOfPerson(quotation.noOfPersons);
      setNoOfSession(quotation.noOfSessions);
      setTotalFee(quotation.totalFee);
    }
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
    >
      <CustomTableScrollbar
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 700,
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          overflow: 'hidden',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            backgroundColor: theme.palette.primary.main,
            padding: '12px 16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            position: 'sticky',
            top: 0,
            zIndex: 1,
          }}
        >
          <Typography variant="h6" sx={{ color: '#fff', fontWeight: 'bold' }}>
            Edit Enquiry Quotation
          </Typography>
          <IconButton onClick={onClose} sx={{ color: '#fff' }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ padding: '20px', overflowY: 'auto', flexGrow: 1 }}>
          <form onSubmit={handleSave}>
            <TextField
              label="Quotation Date"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={quotationDate}
              onChange={(e) => setQuotationDate(e.target.value)}
              sx={{
                mb: 3,
                '& .MuiInputLabel-root': { color: theme.palette.text.primary },
                '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.primary.main },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
              }}
            />
            <TextField
              label="Candidate Name"
              variant="outlined"
              fullWidth
              value={candidateName}
              onChange={(e) => setCandidateName(e.target.value)}
              sx={{
                mb: 3,
                '& .MuiInputLabel-root': { color: theme.palette.text.primary },
                '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.primary.main },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
              }}
            />
            <TextField
              label="Mobile No."
              variant="outlined"
              fullWidth
              value={mobileNo}
              onChange={(e) => setMobileNo(e.target.value)}
              sx={{
                mb: 3,
                '& .MuiInputLabel-root': { color: theme.palette.text.primary },
                '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.primary.main },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
              }}
            />
            <TextField
              label="Email"
              variant="outlined"
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={{
                mb: 3,
                '& .MuiInputLabel-root': { color: theme.palette.text.primary },
                '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.primary.main },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
              }}
            />
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id="country-label" sx={{ color: theme.palette.text.primary }}>Country</InputLabel>
              <Select
                labelId="country-label"
                id="country"
                value={countryId}
                label="Country"
                onChange={(e) => setCountryId(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
                }}
              >
                {staticCountryData.map((countryItem) => (
                  <MenuItem key={countryItem.countryId} value={countryItem.countryId}>
                    {countryItem.countryName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id="state-label" sx={{ color: theme.palette.text.primary }}>State</InputLabel>
              <Select
                labelId="state-label"
                id="state"
                value={stateId}
                label="State"
                onChange={(e) => setStateId(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
                }}
              >
                {staticStateData.map((stateItem) => (
                  <MenuItem key={stateItem.stateId} value={stateItem.stateId}>
                    {stateItem.stateName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id="city-label" sx={{ color: theme.palette.text.primary }}>City</InputLabel>
              <Select
                labelId="city-label"
                id="city"
                value={cityId}
                label="City"
                onChange={(e) => setCityId(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
                }}
              >
                {staticCityData.map((cityItem) => (
                  <MenuItem key={cityItem.cityId} value={cityItem.cityId}>
                    {cityItem.cityName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="No. of Person"
              variant="outlined"
              fullWidth
              value={noOfPerson}
              onChange={(e) => setNoOfPerson(digitsOnly(e.target.value))}
              inputProps={{ inputMode: 'numeric' }}
              sx={{
                mb: 3,
                '& .MuiInputLabel-root': { color: theme.palette.text.primary },
                '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.primary.main },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
              }}
            />
            <TextField
              label="No. of Session"
              variant="outlined"
              fullWidth
              value={noOfSession}
              onChange={(e) => setNoOfSession(digitsOnly(e.target.value))}
              inputProps={{ inputMode: 'numeric' }}
              sx={{
                mb: 3,
                '& .MuiInputLabel-root': { color: theme.palette.text.primary },
                '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.primary.main },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
              }}
            />
            <TextField
              label="Total Fee"
              variant="outlined"
              fullWidth
              value={totalFee}
              onChange={(e) => setTotalFee(decimalOnly(e.target.value))}
              inputProps={{ inputMode: 'decimal' }}
              sx={{
                mb: 3,
                '& .MuiInputLabel-root': { color: theme.palette.text.primary },
                '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.primary.main },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
              }}
            />
            <TextField
              label="Course"
              variant="outlined"
              fullWidth
              value={course}
              onChange={(e) => setCourse(e.target.value)}
              sx={{
                mb: 3,
                '& .MuiInputLabel-root': { color: theme.palette.text.primary },
                '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.primary.main },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Tooltip
                title={
                  !candidateName ? "Enter Candidate Name" :
                  !mobileNo ? "Enter Mobile No." :
                  !email ? "Enter Email" :
                  !countryId ? "Select Country" :
                  !stateId ? "Select State" :
                  !cityId ? "Select City" :
                  !noOfPerson ? "Enter No. of Person" :
                  !noOfSession ? "Enter No. of Session" :
                  !totalFee ? "Enter Total Fee" : "Enter Course"
                }
                arrow
              >
                <span>
                  <Button
                    variant="contained"
                    onClick={handleSave}
                    sx={{
                      mr: 2,
                      backgroundColor: theme.palette.primary.main,
                      '&:hover': { backgroundColor: theme.palette.primary.dark },
                    }}
                    disabled={!candidateName || !mobileNo || !email || !countryId || !stateId || !cityId || !noOfPerson || !noOfSession || !totalFee || !course}
                  >
                    UPDATE
                  </Button>
                </span>
              </Tooltip>
              <Button
                variant="contained"
                color="error"
                onClick={handleCancel}
              >
                CANCEL
              </Button>
            </Box>
          </form>
        </Box>
      </CustomTableScrollbar>
    </Modal>
  );
};

export default EnquiryQuotation;
